import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDirection } from '../../../contexts/DirectionContext';

interface CountdownProps {
  targetDate: Date;
  className?: string;
}

interface TimeLeft {
  weeks: number;
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  total: number;
}

const calculateTimeLeft = (targetDate: Date): TimeLeft => {
  const difference = targetDate.getTime() - new Date().getTime();
  const total = Math.max(0, difference);
  
  return {
    weeks: Math.floor(total / (1000 * 60 * 60 * 24 * 7)),
    days: Math.floor((total / (1000 * 60 * 60 * 24)) % 7),
    hours: Math.floor((total / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((total / 1000 / 60) % 60),
    seconds: Math.floor((total / 1000) % 60),
    total
  };
};

export const Countdown: React.FC<CountdownProps> = ({
  targetDate,
  className = ''
}) => {
  const { t } = useTranslation();
  const { isRTL } = useDirection();
  const [timeLeft, setTimeLeft] = useState<TimeLeft>(calculateTimeLeft(targetDate));

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft(targetDate));
    }, 1000);

    return () => clearInterval(timer);
  }, [targetDate]);

  if (timeLeft.total === 0) {
    return <span className={className}>{t('lives.startsIn.now')}</span>;
  }

  // If more than 24 hours away, show days and hours
  if (timeLeft.total > 24 * 60 * 60 * 1000) {
    const parts: string[] = [];
    
    if (timeLeft.weeks > 0) {
      parts.push(`${timeLeft.weeks}${t('time.weekShort')}`);
    }
    if (timeLeft.days > 0 || timeLeft.weeks > 0) {
      parts.push(`${timeLeft.days}${t('time.dayShort')}`);
    }
    parts.push(`${timeLeft.hours}${t('time.hourShort')}`);
    
    const separator = isRTL ? ' : ' : ' : ';
    return (
      <span className="inline-flex flex-row-reverse items-center gap-2" dir="ltr">
        {parts.map((part, index) => (
          <React.Fragment key={index}>
            <span className={className}>{part}</span>
            {index < parts.length - 1 && <span className="opacity-50 text-white">{separator}</span>}
          </React.Fragment>
        ))}
      </span>
    );
  }

  // If less than 24 hours away, show detailed countdown
  const formattedHours = String(timeLeft.hours).padStart(2, '0');
  const formattedMinutes = String(timeLeft.minutes).padStart(2, '0');
  const formattedSeconds = String(timeLeft.seconds).padStart(2, '0');

  return (
    <span className="inline-flex items-center gap-2" dir="ltr">
      <span className={className}>{formattedHours}</span>
      <span className="opacity-50 text-white">:</span>
      <span className={className}>{formattedMinutes}</span>
      <span className="opacity-50 text-white">:</span>
      <span className={className}>{formattedSeconds}</span>
    </span>
  );
};

export default Countdown; 